'use client'

import { useEffect } from 'react'
import { HERO_BANNERS } from './HeroBanners'

export default function HeroAnimator() {
  useEffect(() => {
    const titleEl = document.querySelector<HTMLElement>('[data-hero-title-text]')
    const messageEl = document.querySelector<HTMLElement>('[data-hero-message]')
    const ctaEl = document.querySelector<HTMLAnchorElement>('[data-hero-cta]')
    const heroSection = document.querySelector<HTMLElement>('.hero-section')

    if (!titleEl || !messageEl || !heroSection) return

    // CTA smooth-scroll handler (preserves original behavior)
    const handleCtaClick = (e: MouseEvent) => {
      e.preventDefault()
      const element = document.querySelector('#contact') as HTMLElement | null
      if (element) {
        const headerOffset = 80
        const elementPosition = element.getBoundingClientRect().top
        const offsetPosition = elementPosition + window.scrollY - headerOffset
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' })
      }
    }
    ctaEl?.addEventListener('click', handleCtaClick)

    // Parallax
    const handleScroll = () => {
      const scrolled = window.scrollY
      heroSection.style.transform = `translateY(${scrolled * -0.5}px)`
    }
    window.addEventListener('scroll', handleScroll, { passive: true })

    // Cursor element appended after hydration to avoid SSR/CSR mismatch
    const cursor = document.createElement('span')
    cursor.className = 'typing-cursor'
    cursor.textContent = '_'
    titleEl.appendChild(cursor)

    let cursorVisible = true
    const cursorInterval = setInterval(() => {
      cursorVisible = !cursorVisible
      cursor.classList.toggle('cursor-hidden', !cursorVisible)
    }, 530)

    let currentIndex = 0
    let charIndex = HERO_BANNERS[0].title.length // initial banner already fully rendered
    let typingTimeout: ReturnType<typeof setTimeout> | undefined
    let cancelled = false

    const setTitleText = (text: string) => {
      const first = titleEl.firstChild
      if (first && first.nodeType === Node.TEXT_NODE) {
        first.textContent = text
      } else {
        titleEl.insertBefore(document.createTextNode(text), cursor)
      }
    }

    const animateTyping = () => {
      if (cancelled) return
      const banner = HERO_BANNERS[currentIndex]
      const fullText = banner.title

      if (charIndex < fullText.length) {
        setTitleText(fullText.substring(0, charIndex + 1))
        if (charIndex === 0) {
          messageEl.style.transition = 'opacity 1s ease'
          messageEl.style.opacity = '0'
          setTimeout(() => {
            messageEl.textContent = banner.message
            setTimeout(() => {
              messageEl.style.transition = 'opacity 3s ease'
              messageEl.style.opacity = '0.85'
            }, 50)
          }, 1000)
        }
        charIndex++
        typingTimeout = setTimeout(animateTyping, 16)
      } else {
        // Hold then advance
        typingTimeout = setTimeout(() => {
          if (cancelled) return
          titleEl.parentElement!.style.opacity = '0'
          titleEl.parentElement!.style.transform = 'translateY(-20px)'
          setTimeout(() => {
            if (cancelled) return
            currentIndex = (currentIndex + 1) % HERO_BANNERS.length
            charIndex = 0
            setTitleText('')
            titleEl.parentElement!.style.opacity = '1'
            titleEl.parentElement!.style.transform = 'translateY(0)'
            setTimeout(animateTyping, 300)
          }, 500)
        }, 8000)
      }
    }

    // Wait until first paint completes before kicking off the animation
    const start = () => {
      // Hold the initial banner briefly so users can read it before it cycles
      typingTimeout = setTimeout(() => {
        if (cancelled) return
        // Advance directly to next banner (skip retyping the initial one)
        titleEl.parentElement!.style.opacity = '0'
        titleEl.parentElement!.style.transform = 'translateY(-20px)'
        setTimeout(() => {
          if (cancelled) return
          currentIndex = 1
          charIndex = 0
          setTitleText('')
          titleEl.parentElement!.style.opacity = '1'
          titleEl.parentElement!.style.transform = 'translateY(0)'
          animateTyping()
        }, 500)
      }, 8000)
    }

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void
    }
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(start, { timeout: 1500 })
    } else {
      setTimeout(start, 800)
    }

    return () => {
      cancelled = true
      window.removeEventListener('scroll', handleScroll)
      ctaEl?.removeEventListener('click', handleCtaClick)
      if (typingTimeout) clearTimeout(typingTimeout)
      clearInterval(cursorInterval)
      if (cursor.parentNode) cursor.parentNode.removeChild(cursor)
    }
  }, [])

  return null
}
